import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DialogComponentService } from '@merchant-portal/app/components/dialog/dialog.component.service';
import { UtilService } from '@merchant-portal/app/services/util.service';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, from, Subject } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { ILoginApiResponse, ILoginSubmit } from '../../../models/login/login.interface';
import { AuthService } from '../../auth.service';
import { FingerprintService } from '../../fingerprint/fingerprint.service';
import { OtpStoreFactory } from '../../otp/factory/otp-store.factory';
import { LoginParams, LoginState, LoginStoreFactory } from '../factory/login-store.factory';

const initialState: LoginState = {
  errorMessage: '',
  isOtpRequired: false,
  isTotpMode: false,
  isCountDownStarted: false,
  isDeviceRecognized: false,
  isLoading: false,
  resendDelaySeconds: 30,
  twoFactorAuthMethod: null,
};

@Injectable()
export class LoginStoreService
  extends ComponentStore<LoginState>
  implements LoginStoreFactory, OnDestroy
{
  private unsubscribe$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private dialogComponentService: DialogComponentService,
    private utilService: UtilService,
    private fingerprintService: FingerprintService,
    private otpStore: OtpStoreFactory
  ) {
    super(initialState);
  }

  errorMessage$ = this.select(state => state.errorMessage);
  isOtpRequired$ = this.select(state => state.isOtpRequired);
  isTotpMode$ = this.select(state => state.isTotpMode);
  isCountDownStarted$ = this.select(state => state.isCountDownStarted);
  isDeviceRecognized$ = this.select(state => state.isDeviceRecognized);
  isLoading$ = this.select(state => state.isLoading);
  resendDelaySeconds$ = this.select(state => state.resendDelaySeconds);
  twoFactorAuthMethod$ = this.select(state => state.twoFactorAuthMethod);
  vm$ = this.select({
    errorMessage: this.errorMessage$,
    isOtpRequired: this.isOtpRequired$,
    isTotpMode: this.isTotpMode$,
    isCountDownStarted: this.isCountDownStarted$,
    isDeviceRecognized: this.isDeviceRecognized$,
    isLoading: this.isLoading$,
    resendDelaySeconds: this.resendDelaySeconds$,
    twoFactorAuthMethod: this.twoFactorAuthMethod$,
  });

  readonly login = this.effect<LoginParams>(loginParams$ =>
    loginParams$.pipe(
      tap(() => this.patchState({ isLoading: true })),
      switchMap(({ email, password, isRemember }) =>
        from(this.getFingerprint(email)).pipe(
          switchMap(fingerprint =>
            this.processLogin(email, password, isRemember, fingerprint).pipe(
              tap(res => {
                this.patchState({ isLoading: false });
                this.handleLoginResponse(res, email, isRemember, fingerprint);
              }),
              catchError((error: HttpErrorResponse) => {
                this.patchState({ isLoading: false });
                return this.handleLoginError(error);
              })
            )
          )
        )
      )
    )
  );

  // Helper function: Get fingerprint
  private getFingerprint(email: string): Promise<string> {
    return this.fingerprintService.getFingerprint({
      email,
      event: 'login',
    });
  }

  // Helper function: Process login
  private processLogin(email: string, password: string, isRemember: boolean, fingerprint: string) {
    const loginForm: ILoginSubmit = { email, password, isRemember };
    return this.authService.login(loginForm, fingerprint);
  }

  // Helper function: Handle login response
  private handleLoginResponse(
    res: ILoginApiResponse,
    email: string,
    isRemember: boolean,
    fingerprint: string
  ): void {
    this.authService.setFingerPrintId(email, fingerprint);

    if (isRemember) {
      this.authService.setIsRemember();
    }

    if (res?.data?.accessToken) {
      this.handleAccessToken(res.data, email);
    } else if (res?.data?.token) {
      // Check if twoFactorAuthMethod is TOTP
      if (res.data.twoFactorAuthMethod === 'TOTP') {
        this.handleTOTPToken(res.data.token, email);
      } else {
        this.handleOtpToken(res.data.token, email, res.data.resendDelaySeconds);
      }
    } else {
      this.handleFallbackToken(
        res.data.token ?? res.data.accessToken,
        email,
        res.data.resendDelaySeconds
      );
    }
  }

  // Helper function: Handle access token
  private handleAccessToken(data: any, email: string): void {
    this.patchState({ isDeviceRecognized: true });
    this.authService.setAccessToken(data.accessToken || '');

    if (data.userInfo) {
      this.authService.setUserInfo(data.userInfo);
      this.authService.setRefreshToken(data.refreshToken || '');
    }

    this.checkAccessToken();
  }

  // Helper function: Handle OTP token
  private handleOtpToken(token: string, email: string, resendDelaySeconds?: number): void {
    this.patchState({
      isOtpRequired: true,
      errorMessage: '',
      isCountDownStarted: true,
      resendDelaySeconds: resendDelaySeconds || 30,
      twoFactorAuthMethod: 'OTP',
    });
    this.authService.setTempToken(token);
    this.authService.setEmail(email);

    // Set OTP store state and save session immediately when we get the token
    this.otpStore.setInitialSession(token, resendDelaySeconds || 30);
    this.otpStore.saveOtpSession();
  }

  // Helper function: Handle fallback token
  private handleFallbackToken(token: string, email: string, resendDelaySeconds?: number): void {
    this.patchState({
      isOtpRequired: true,
      errorMessage: '',
      isCountDownStarted: true,
      resendDelaySeconds: resendDelaySeconds || 30,
      twoFactorAuthMethod: 'OTP', // Default to OTP for fallback
    });
    this.authService.setTempToken(token);
    this.authService.setEmail(email);

    // Set OTP store state and save session immediately when we get the token
    this.otpStore.setInitialSession(token, resendDelaySeconds || 30);
    this.otpStore.saveOtpSession();
  }

  // Helper function: Handle TOTP token (when twoFactorAuthMethod is TOTP)
  private handleTOTPToken(token: string, email: string): void {
    this.patchState({
      isTotpMode: true,
      isOtpRequired: true, // Still need this for the OTP component to render
      errorMessage: '',
      isCountDownStarted: false, // No countdown for TOTP
      twoFactorAuthMethod: 'TOTP',
    });
    this.authService.setTempToken(token);
    this.authService.setEmail(email);
  }

  // Helper function: Handle login errors
  private handleLoginError({ error }: HttpErrorResponse) {
    if (error.errors.includes('blocked')) {
      this.showBlockedDialog(error.errors);
      return EMPTY;
    }

    if (error.errors.includes('suspended')) {
      const suspendedTime = this.utilService.parseSuspendedTime(error.errors);
      if (suspendedTime) {
        const remainingMinutes = this.utilService.getRemainingMinutes(suspendedTime);
        this.showSuspendedDialog(remainingMinutes);
      }
    } else if (error.errors.includes('please wait')) {
      this.patchState({
        isOtpRequired: false,
        errorMessage: error.errors,
        isCountDownStarted: false,
      });
    } else {
      this.patchState({ errorMessage: error.errors });
    }
    return EMPTY;
  }

  showSuspendedDialog(suspendedTime: number) {
    this.dialogComponentService.openConfirmationDialog({
      width: '530px',
      height: '275px',
      closable: true,
      body: {
        title: 'Code Request Suspended',
        message: `Your code verification request is currently suspended.<br>Please try again after ${suspendedTime} minutes.`,
        closeText: 'Close',
        confirmText: 'Contact Helpdesk',
        isDoNothing: true,
      },
    });
  }

  showBlockedDialog(message?: string) {
    let title = 'Account Blocked';
    let subTitle =
      "You've entered too many incorrect password. Please contact helpdesk for assistance.";

    if (message?.toLowerCase() === 'merchant is blocked') {
      title = 'Merchant Account Blocked';
      subTitle = 'Merchant account is unavailable. Please contact helpdesk for assistance.';
    }

    this.dialogComponentService
      .openConfirmationDialog({
        width: '400px',
        height: '385px',
        closable: false,
        body: {
          title: title,
          message: subTitle,
          closeText: 'Close',
          confirmText: 'Contact Helpdesk',
          illustration: '/assets/icon/pin-unsuccessfull.svg',
        },
      })
      .onClose.pipe(take(1))
      .subscribe((confirmed: boolean) => {
        window.location.reload();
        return;
      });
  }

  disableOtpRequire = () => {
    this.patchState({
      isOtpRequired: false,
      isTotpMode: false,
    });
    // Clear OTP session when manually disabling OTP
    localStorage.removeItem('otpSession');
  };

  checkAccessToken() {
    if (this.authService.getAccessToken()) {
      this.router.navigateByUrl('/');
    }
  }

  getFingerPrintId(email: string) {
    return this.authService.getFingerPrintId(email) || '';
  }

  readonly checkStoredFingerprint = this.effect<string>(trigger$ =>
    trigger$.pipe(
      switchMap(email =>
        from(this.fingerprintService.getFingerprint()).pipe(
          tap(currentFingerprint => {
            const storedFingerprint = this.getFingerPrintId(email);
            if (storedFingerprint && storedFingerprint === currentFingerprint) {
              this.patchState({ isDeviceRecognized: true });
            } else {
              this.patchState({ isDeviceRecognized: false });
            }
          })
        )
      )
    )
  );

  resetDeviceRecognition() {
    this.patchState({ isDeviceRecognized: false });
  }

  setResendDelaySeconds(seconds: number) {
    this.patchState({ resendDelaySeconds: seconds });
  }

  checkExistingOtpSession(email: string): boolean {
    const sessionDataStr = localStorage.getItem('otpSession');
    if (sessionDataStr) {
      const sessionData = JSON.parse(sessionDataStr);
      const currentTime = Date.now();
      const timeDiff = Math.floor((currentTime - sessionData.timestamp) / 1000);
      const remainingCountdown = Math.max(0, sessionData.countdown - timeDiff);

      // Check if session belongs to the same email and has remaining time
      return remainingCountdown > 0 && sessionData.email === email && !!sessionData.otpToken;
    }
    return false;
  }

  resumeOtpSession() {
    this.patchState({
      isOtpRequired: true,
      isCountDownStarted: true,
      errorMessage: '',
    });
  }

  override ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    super.ngOnDestroy();
  }
}
